<template>
  <el-dialog
    title="配置已选方案"
    :visible.sync="visible"
    width="1080px"
    :close-on-click-modal="false"
    :append-to-body="true"
    :destroy-on-close="true"
    @open="handleOpen"
    @close="handleClose"
  >
    <div class="dialog-content">
      <el-alert
        :title="`每个城市下的同年款的相同车型，最多可上架${MAX_PLAN_COUNT}个“纯租”类型的个人租赁方案。`"
        type="info"
        :closable="false"
      />

      <SelectionTable
        v-if="visible"
        v-model="selectedPlans"
        :columns="columns"
        :data="tableData"
        :loading="loading"
        :pagination="pagination"
        :custom-selectable="checkCityQuota"
        @change="handleTableChange"
        @select="handlePlanSelect"
      >
        <template #search>
          <div class="search-area">
            <div class="search-form">
              <div class="form-item">
                <span class="label">租赁方案</span>
                <QS-select
                  v-model="searchForm.rentPeriodType"
                  :optionData="rentPeriodTypeOptions"
                  :optionConfig="{
                    valueKey: 'value',
                    labelKey: 'label',
                  }"
                  filterable
                  placeholder="请选择"
                  @change="handleRentTypeChange"
                />
                <el-button
                  type="primary"
                  class="search-btn"
                  @click="handleSearch"
                >
                  查询
                </el-button>
              </div>
            </div>
          </div>
        </template>
      </SelectionTable>
    </div>

    <template #footer>
      <div class="footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button
          type="primary"
          :disabled="selectedPlans.length === 0"
          @click="handleConfirm"
        >
          保存
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import {
  getBatchPlanList,
  getModelSelectedPlansAndCityQuota,
  saveBatchPlans,
  getSinglePlan,
} from '@/api/goods'
import SelectionTable from '@/components/SelectionTable'
import { Message, Tooltip } from '@qs/ui'
import { computed, reactive, ref } from 'vue'
import { useBatchSession } from '../hooks/useBatchSession'
import { use$to, communicationHandler } from '@/utils/useUtils'

const $to = use$to()
const emit = defineEmits(['confirm'])

const { sessionId } = useBatchSession()

// 常量定义
const MAX_PLAN_COUNT = 6 // 每个城市最多可选方案数量

const visible = ref(false)
const loading = ref(false)

// 当前操作的车型信息
const currentModel = ref(null)

// 城市配额映射表 { cityCode: 剩余配额数 }
const cityQuotaMap = ref({})

// 搜索表单
const searchForm = reactive({
  rentPeriodType: 3, // 默认月租
})

// 租赁周期选项
const rentPeriodTypeOptions = [
  { label: '日租', value: 1 },
  { label: '月租', value: 3 },
]

// 分页
const pagination = ref({
  pageNum: 1,
  pageSize: 10,
  total: 0,
})

// 列表数据
const tableData = ref([])
// 已选择的方案
const selectedPlans = ref([])
// 编辑的方案相关信息
const editSchemeInfo = ref({
  id: null,
  cityCode: null,
})
// 表格列配置
const columns = computed(() => [
  {
    name: 'planId',
    columnProps: {
      prop: 'planId',
      label: '租赁方案ID',
      width: 100,
    },
  },
  {
    name: 'cityName',
    columnProps: {
      prop: 'cityName',
      label: '城市',
      width: 100,
    },
  },
  {
    name: 'planName',
    columnProps: {
      prop: 'planName',
      label: '租赁方案名称',
      minWidth: 180,
      showOverflowTooltip: true,
    },
  },
  {
    name: 'rentPeriodInfo',
    columnProps: {
      prop: 'rentPeriodInfo',
      label: '租期',
      width: 100,
    },
  },
  {
    name: 'deposit',
    columnProps: {
      prop: 'deposit',
      label: '押金总额',
      width: 100,
    },
    render: ({ row }) => {
      if (!row.deposit) return '--'
      return `${row.deposit}元`
    },
  },
  {
    name: 'monthlyRent',
    columnProps: {
      prop: 'monthlyRent',
      label: '租金',
    },
    render: ({ row }) => {
      if (!row.rentAmount) return '--'
      return `${row.rentAmount}元/${row.rentAmountUnit}`
    },
  },
  {
    name: 'otherFees',
    columnProps: {
      prop: 'otherFees',
      label: '其他费项',
      width: 100,
    },
    render: ({ row }) => {
      if (!row.otherFees || row.otherFees?.length === 0) return '--'

      const formatFee = (({ name, payDetailString } = {}) => `${name}：${payDetailString}`)
      if (row.otherFees?.length === 1) {
        return formatFee(row.otherFees?.[0])
      }
      return (
        <div>
          <span>{formatFee(row.otherFees?.[0])}</span>
          <Tooltip
            effect="dark"
            content={row.otherFees?.map(formatFee)?.join('、')}
            placement="top"
          >
            <span class="a-hover-link">等{row.otherFees?.length}项</span>
          </Tooltip>
        </div>
      )
    },
  },
  {
    name: 'presentDays',
    columnProps: {
      prop: 'presentDays',
      label: '赠送天数',
      width: 80,
    },
    render: ({ row }) => `${row.presentDays || 0}天`,
  },
  {
    name: 'operate',
    columnProps: {
      label: '操作',
      fixed: 'right',
    },
    getOperateList: ({ row }) => {
      return [{
        name: 'edit',
        label: '编辑',
        action: () => editScheme(row),
      }]
    },
    type: 'operate',
  },
])

// 处理表格变更（分页）
const handleTableChange = ({ pagination: pager }) => {
  pagination.value.pageSize = pager.pageSize
  pagination.value.pageNum = pager.pageNum
  loadData()
}

// 处理方案选择变化，更新城市配额
const handlePlanSelect = (selection, row) => {
  const { cityCode } = row
  if (cityQuotaMap.value[cityCode] === undefined) return

  // 检查该行是否在当前选中列表中（事件触发时 selectedPlans 已更新）
  const isSelected = selectedPlans.value.some((p) => p.id === row.id)

  if (isSelected) {
    // 选中：配额 -1
    cityQuotaMap.value[cityCode]--
  } else {
    // 反选：配额 +1
    cityQuotaMap.value[cityCode]++
  }
}

// 检查某个城市是否已达配额上限
const isCityQuotaFull = (cityCode) => {
  const quota = cityQuotaMap.value[cityCode]
  // 如果没有配额数据，不限制
  if (quota === undefined || quota === null) {
    return false
  }
  return quota <= 0
}

// 城市配额检查函数（传递给 SelectionTable）
const checkCityQuota = (row, selectedIds) => {
  // 如果当前行已被选中，允许反选
  if (selectedIds.includes(row.id)) {
    return true
  }

  // 检查该城市是否已达配额
  return !isCityQuotaFull(row.cityCode)
}

// 加载方案列表
const loadData = async () => {
  loading.value = true

  const res = await getBatchPlanList({
    sessionId: sessionId.value,
    carModelId: currentModel.value?.carModelId,
    rentPeriodType: searchForm.rentPeriodType,
    pageNum: pagination.value.pageNum,
    pageSize: pagination.value.pageSize,
  })

  loading.value = false

  if (res?.code === 1) {
    tableData.value = res.data?.items || []
    pagination.value.total = res.data?.totalNum || 0
  } else {
    tableData.value = []
    pagination.value.total = 0
  }
}

// 租赁类型变更
const handleRentTypeChange = () => {
  // 切换租赁类型时不再清空已选方案
  pagination.value.pageNum = 1
  loadData()
}

// 搜索
const handleSearch = () => {
  pagination.value.pageNum = 1
  loadData()
}

// 打开弹窗回调
const handleOpen = async () => {
  // 1. 获取已选方案和配额
  const res = await getModelSelectedPlansAndCityQuota({
    sessionId: sessionId.value,
    carModelId: currentModel.value?.carModelId,
  })

  if (res?.code === 1) {
    selectedPlans.value = res.data?.selectPlanAndCityCodeList || []
    // 保存城市配额映射表
    cityQuotaMap.value = res.data?.cityCode2CountMap || {}
  }

  // 2. 加载方案列表
  await loadData()
}

// 关闭弹窗
const handleClose = () => {
  visible.value = false
}

// 确认保存
const handleConfirm = async () => {
  if (selectedPlans.value.length === 0) {
    Message.warning('请至少选择一个租赁方案')
    return
  }

  // 调用服务端保存
  loading.value = true
  const res = await saveBatchPlans({
    sessionId: sessionId.value,
    carModelId: currentModel.value?.carModelId,
    planAndCodeList: selectedPlans.value.map((p) => p.id),
  })
  loading.value = false

  if (res?.code === 1) {
    Message.success('保存成功')
    emit('confirm', selectedPlans.value)
    handleClose()
  }
  // 失败时 axios 已显示错误提示
}

// 打开弹窗
const open = (model) => {
  currentModel.value = model
  selectedPlans.value = model?.selectedPlans || []
  // 根据已选方案的类型自动设置选择器，没有已选方案时默认月租
  searchForm.rentPeriodType = model.rentPeriodType || 3
  pagination.value.pageNum = 1
  visible.value = true
}

// 编辑租赁方案按钮
const editScheme = (row) => {
  editSchemeInfo.value = {
    id: row.planId,
    cityCode: row.cityCode,
  }
  const newWindow = window.open(`/car_to_lease/leaseScheme/edit?id=${row.planId}&type=1&scene=1`, '_blank')
  const currentWindow = window

  if (newWindow) {
    newWindow.addEventListener('beforeunload', () => {
      setTimeout(() => {
        currentWindow.focus()
      }, 100)
    })
  }
}

// 更新租赁方案列表数据，根据租赁方案id批量更新该页面下数据
const updateTableData = (list, info, planId) => {
  const tempData = list.value.map((item) => {
    if (item.planId === Number(planId)) {
      return info
    }
    return item
  })
  list.value = tempData
}
const handleSchemeEdit = (data) => {
  if (Number(data) === editSchemeInfo.value.id) {
    // 请求列表该条数据，因为租赁方案数据保存到es有延迟，所以需要等待1s
    loading.value = true
    const timer = setTimeout(async () => {
      clearTimeout(timer)
      const [err, res] = await $to(getSinglePlan({
        planId: data,
        cityCode: editSchemeInfo.value.cityCode,
      }))
      loading.value = false
      if (!err && res?.code === 1) {
        // 判断tableData中是否存在该条数据，如果存在更新tableData
        updateTableData(tableData, res.data, data)
        // 判断selectedPlans中是否存在该条数据，如果存在更新selectedPlans
        updateTableData(selectedPlans, res.data, data)
      }
    }, 1000)
  }
}

communicationHandler('lease_scheme_added', {
  lease_scheme_edit: handleSchemeEdit,
})
defineExpose({
  open,
})
</script>

<style lang="scss" scoped>
::v-deep {
  .el-alert__content {
    height: 22px;
  }
}

::v-deep .el-alert {
  padding: 7px 16px;
  margin-bottom: 12px;
}

.search-btn {
  margin-left: 12px;
}

.footer {
  margin-top: 20px;
  text-align: right;
}

.search-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  .search-form {
    .form-item {
      display: flex;
      align-items: center;

      .label {
        padding: 0 12px;
        height: 32px;
        line-height: 32px;
        border-radius: 6px 0 0 6px;
        background: #ffffff;
        border-width: 1px 0 1px 1px;
        border-style: solid;
        border-color: #e7e8eb;
        box-sizing: border-box;
      }
    }
  }
}

::v-deep {
  .el-select {
    .el-input__inner {
      border-radius: 0 6px 6px 0;
      height: 32px;
      line-height: 32px;
      background: #ffffff;
      border: 1px solid #e7e8eb;
      box-sizing: border-box;
    }

    .el-input__inner:hover,
    .el-input__inner:focus {
      border-color: #e7e8eb;
    }
  }
}
</style>
